/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { BazaarVoice } from '@/components/blocks/ProductDetails/parts/BazaarVoice';
import {
	containerManufacturerSX,
	containerMoreTagsSX,
	displayContentSX,
	manufacturerSX,
	productDetailsNameSX,
} from '@/components/blocks/ProductDetails/styles/display';
import {
	NAVIGABLE_SECTION_IDS,
	useScrollToNavigableSection,
} from '@/components/content/ProductDetails/hooks/useScrollToNavigableSection';
import { ProductTag } from '@/components/content/ProductDetails/parts/ProductTag';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { logEventWithValues } from '@/data/utils/logEventsUtils';
import { useSearchTermRedirect } from '@/data/utils/useSearchTermRedirect';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { parseHTML } from '@/utils/parseHTMLModern';
import { isKitOrBundleType } from '@/utils/productIsA';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, MouseEvent, useContext } from 'react';

const ELEMENT_IDS = {
	summaryCard: 'productSummaryCard',
	brand: 'productBrand',
	name: 'productTitle',
	ratings: 'ratingSummary',
	qa: 'qaSummary',
};

const STICKY_ELEMENT_IDS = {
	summaryCard: 'stickyProductSummaryCard',
	brand: 'stickyProductBrand',
	name: 'stickyProductName',
	ratings: 'stickyProductRatings',
	qa: 'stickyProductQA',
};

export const ProductDetailsDisplay: FC<{ isStickyComponent?: boolean }> = ({
	isStickyComponent = false,
}) => {
	const theme = useTheme();
	const RouteLocal = useLocalization('Routes');
	const { selection, product } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	> &
		ReturnType<typeof useSkuListTable>;
	const isKitOrBundle = isKitOrBundleType[product?.type ?? 'unknown'];
	const sku = !isKitOrBundle ? selection?.sku : product;
	const { name, manufacturer, partNumber } = getProductDisplayInfo(sku, product);

	const { settings } = useSettings();
	const isMobileApp = settings.isMobileApp;
	const resolveSearchTermRedirect = useSearchTermRedirect();

	const ids = isStickyComponent ? STICKY_ELEMENT_IDS : ELEMENT_IDS;
	const productName = name?.toLowerCase() ?? '';
	const manufacturerNameFormatted = manufacturer?.toLowerCase() ?? '';
	const manufacturerName = manufacturer ?? '';
	const manufacturerHref = `${RouteLocal.Search.route.t()}/${manufacturerName}`;
	const handleManufacturerClick = (e: MouseEvent) => {
		e.preventDefault();
		void resolveSearchTermRedirect(manufacturerName, manufacturerHref);
	};
	const manufacturerLabel = (
		<Typography component="p" variant="bodyBaseline" id={ids.brand} sx={manufacturerSX}>
			{manufacturerNameFormatted && parseHTML(manufacturerNameFormatted)}
		</Typography>
	);
	const { scrollToNavigableSection } = useScrollToNavigableSection();
	const handleGoProductReviewsSection = () => {
		scrollToNavigableSection({
			sectionId: NAVIGABLE_SECTION_IDS.PRODUCT_REVIEWS,
		});
	};

	return (
		<Stack sx={displayContentSX(theme, isStickyComponent)} id={ids.summaryCard}>
			{manufacturer ? (
				<Box sx={containerManufacturerSX(isStickyComponent)}>
					{isMobileApp ? (
						<a
							onClick={() => logEventWithValues('onBrandClick', manufacturer)}
							role="button "
							tabIndex={0}
						>
							{manufacturerLabel}
						</a>
					) : (
						<Linkable
							href={{
								pathname: manufacturerHref,
							}}
							id={manufacturer}
							data-testid={manufacturer}
							onClick={handleManufacturerClick}
						>
							{manufacturerLabel}
						</Linkable>
					)}
				</Box>
			) : null}
			{name ? (
				<Typography
					variant="headingTitle"
					component="h1"
					id={ids.name}
					sx={productDetailsNameSX(isStickyComponent)}
				>
					{productName && parseHTML(productName)}
				</Typography>
			) : null}
			<Box display="flex" sx={containerMoreTagsSX(theme)}>
				<Box display="flex">
					{isStickyComponent ? null : <ProductTag />}
					<Box id={ids.ratings} onClick={() => handleGoProductReviewsSection()}>
						<BazaarVoice productId={partNumber} isForStarts />
					</Box>
				</Box>
				<Box id={ids.qa}>
					<BazaarVoice productId={partNumber} isForStarts={false} />
				</Box>
			</Box>
		</Stack>
	);
};
